import { MqttClient } from "mqtt";
import {
  alerts,
  freshWeatherData,
  mockConfig,
  mockLocalStorage,
  mockSystemStatus,
  mockEcoSog,
} from "./data";
import { RequestTopics, Topics } from "services/topics";
import { polyModelTransform } from "screens/dashboard/fuel-curve/fuel-curve-graph/fuel-curve-utils";

export enum SimulationMode {
  // Ping pong between upper and lower limits
  PingPong,
  // Random walk between upper and lower limits, with fuel rate simulation
  Brownian,
}

type SimulationOptions = {
  simulateSpeed?: boolean;
  simulateEcoSpeed?: boolean;
  simulateImpacts?: boolean;
  mode?: SimulationMode;
};

const frequency = 350;
const simulationRpmRange = { lower: 1200, upper: 3000 };

export const simulateTrip = (
  client: MqttClient,
  options: SimulationOptions
) => {
  // static data
  client.publish(Topics.PairingCode, "KT4D62G0");
  client.publish(Topics.Rog, "30");
  client.publish(Topics.Duration, "1234");
  client.publish(Topics.Distance, "6.223");
  client.publish(Topics.SafetyQuality, "0.7");
  client.publish(Topics.PeakImpact, "3.5");
  client.publish(Topics.RedImpacts, "4");
  client.publish(Topics.HasEcoSog, JSON.stringify({ enabled: true }));
  client.publish(Topics.WeatherData, JSON.stringify(freshWeatherData()));
  client.publish(Topics.EcoSog, JSON.stringify(mockEcoSog));

  // initial values
  let speed = 20;
  let rpm = simulationRpmRange.lower;
  let fuelRate = 0;
  let increasing = true;

  // intervals
  let speedInterval: NodeJS.Timer;
  let impactInterval: NodeJS.Timer;
  let ecoSpeedInterval: NodeJS.Timer;
  let positionInterval: NodeJS.Timer;

  if (options.simulateSpeed) {
    // GPS position; the frontend only uses message arrival to derive GPS
    // connection status, so toggling speed simulation off shows GPS offline
    let latitude = 64.1466;
    let longitude = -21.9426;
    positionInterval = setInterval(() => {
      latitude += (speed / 3600 / 60) * Math.random();
      longitude += (speed / 3600 / 60) * Math.random();
      client.publish(
        Topics.Position,
        JSON.stringify({ latitude: latitude, longitude: longitude })
      );
    }, 1000);
  }

  if (options.simulateSpeed) {
    if (options.mode === SimulationMode.Brownian) {
      // Random walk between upper and lower limits
      speedInterval = setInterval(() => {
        if (rpm <= simulationRpmRange.lower) {
          increasing = true;
        } else if (rpm >= simulationRpmRange.upper) {
          increasing = false;
        } else {
          if (Math.random() < 0.1) {
            increasing = !increasing;
          }
        }
        const factor = 0.2 + Math.random() * 0.2;
        if (increasing) {
          speed += 1 * factor;
          rpm += 100 * factor;
        } else {
          speed -= 1 * factor;
          rpm -= 100 * factor;
        }

        fuelRate = rpm * 0.11 + Math.random() / 10;

        client.publish(Topics.Sog, speed.toString());
        client.publish(Topics.Rpm, rpm.toString());
      }, frequency);
    } else {
      // Ping pong between upper and lower limits
      speedInterval = setInterval(() => {
        if (speed >= 34) increasing = false;
        if (speed <= 20) increasing = true;
        increasing ? (speed += 1) : (speed -= 1);
        increasing ? (rpm += 100) : (rpm -= 100);

        client.publish(Topics.Sog, speed.toString());
        client.publish(Topics.Rpm, rpm.toString());
      }, frequency);
    }
  }

  if (options.simulateImpacts) {
    impactInterval = setInterval(() => {
      client.publish(Topics.Impact, (Math.random() * 0.4).toString());
    }, frequency);
  }

  if (options.simulateEcoSpeed) {
    ecoSpeedInterval = setInterval(() => {
      // Simulate running average fuel rate
      if (mockEcoSog?.avg_fuel_rate_l_nm?.["30s"]) {
        if (mockEcoSog.fuel_curve_model_corrected_coef) {
          const polyModel = mockEcoSog.fuel_curve_model_corrected_coef;
          const fuelRateOffset = Math.cos(speed / 10) * 0.3;
          mockEcoSog.avg_fuel_rate_l_nm["30s"] =
            polyModelTransform([speed], polyModel)[0] + fuelRateOffset;
        } else {
          mockEcoSog.avg_fuel_rate_l_nm["30s"] = fuelRate / 100;
        }
      }

      // Simulate fuel savings
      if (mockEcoSog?.fuel_deviation_from_pred?.trip) {
        mockEcoSog.fuel_deviation_from_pred.trip -= 0.003;
      }

      mockEcoSog.max_sog = Math.max(speed, mockEcoSog.max_sog || 0);

      client.publish(Topics.EcoSog, JSON.stringify(mockEcoSog));
    }, 100);
  }

  return () => {
    clearInterval(speedInterval);
    clearInterval(impactInterval);
    clearInterval(ecoSpeedInterval);
    clearInterval(positionInterval);
  };
};

export const simulateRespones = (client: MqttClient) => {
  const handleMockResponse = (topic, message) => {
    if (topic === RequestTopics.ThresholdConfig) {
      try {
        const request = JSON.parse(message.toString());
        client.publish(
          request.response_topic,
          JSON.stringify({
            code: 200,
            status: "OK",
            message: "Successfully update system settings",
          })
        );
      } catch (error) {
        console.error(error);
      }
    }

    if (topic === RequestTopics.TransferData) {
      let request: {
        response_topic: string;
        status: string;
      } = { response_topic: "", status: "START" };

      try {
        request = JSON.parse(message.toString());
      } catch (error) {
        console.error(error);
      }

      let progress = 0;

      if (request.status === "STOP") {
        client.publish(
          request.response_topic,
          JSON.stringify({
            code: 499,
            status: "CANCELLED",
            message: "The request has been cancelled by the client",
          })
        );
        return;
      }

      const progressInterval = setInterval(() => {
        client.publish(
          request.response_topic,
          JSON.stringify({
            code: 102,
            message: "PROGRESS",
            data: {
              trip_files_transferred: 10,
              total_trip_files: 100,
              progress: progress,
              estimated_time_remaining: 30,
            },
          })
        );

        progress += 0.1;

        if (progress > 1) {
          clearInterval(progressInterval);
          client.publish(
            request.response_topic,
            JSON.stringify({
              code: 200,
              status: "OK",
              message: "Successfully transferred data to USB drive",
              data: {
                trip_files_transferred: 100,
                total_trip_files: 100,
                progress: progress,
                estimated_time_remaining: 0,
              },
            })
          );
        }
      }, 200);
    }

    if (topic === RequestTopics.Alerts) {
      try {
        const request = JSON.parse(message.toString());
        client.publish(
          request.response_topic,
          JSON.stringify({
            data: [...alerts, ...alerts, ...alerts],
          })
        );
      } catch (error) {
        console.error(error);
      }
    }

    if (topic === RequestTopics.Config) {
      try {
        const request = JSON.parse(message.toString());
        client.publish(
          request.response_topic,
          JSON.stringify({ data: mockConfig })
        );
      } catch (error) {
        console.error(error);
      }
    }

    if (topic === RequestTopics.TripCaptain) {
      try {
        const request = JSON.parse(message.toString());
        client.publish(request.response_topic, JSON.stringify({ code: 200 }));
        const captainId = request.selected_captain.id;
        const captainObj = mockConfig?.captains?.find(
          (cap) => cap.id === captainId
        );

        if (!captainId) {
          client.publish(Topics.TripCaptain, "null");
        } else {
          client.publish(
            Topics.TripCaptain,
            captainObj
              ? JSON.stringify(captainObj)
              : JSON.stringify({
                  id: captainId,
                  name: "Unknown",
                  rank: 0,
                })
          );
        }
      } catch (error) {
        console.error(error);
      }
    }

    if (topic === RequestTopics.SystemStatus) {
      try {
        const request = JSON.parse(message.toString());
        client.publish(
          request.response_topic,
          JSON.stringify({ data: mockSystemStatus })
        );
      } catch (error) {
        console.error(error);
      }
    }

    if (topic === RequestTopics.StartNewTrip) {
      try {
        const request = JSON.parse(message.toString());
        client.publish(request.response_topic, JSON.stringify({ code: 200 }));
      } catch (error) {
        console.error(error);
      }
    }

    if (topic === RequestTopics.LocalStorage) {
      try {
        const request = JSON.parse(message.toString());

        if (request?.method === "GET_ALL") {
          client.publish(
            request.response_topic,
            JSON.stringify({
              code: 200,
              data: mockLocalStorage ?? null,
            })
          );
        }

        if (request?.method === "GET") {
          client.publish(
            request.response_topic,
            JSON.stringify({
              code: 200,
              data: mockLocalStorage[request.body] ?? null,
            })
          );
        }

        if (request?.method === "PUT") {
          client.publish(
            request.response_topic,
            JSON.stringify({
              code: 200,
            })
          );
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  client.on("message", handleMockResponse);

  return () => {
    client?.removeListener("message", handleMockResponse);
  };
};
